/**
 * Pantalla de **historial de operaciones de talonarios** (paginado + filtros).
 * Sirve `ROUTES.talonarioHistory` (`/talonarios/historial`). Equivale a
 * `talonario_history.html` del SSR.
 *
 * Exige `manage_talonarios` (vía `RequireManageTalonarios`). Orquesta el filtro de
 * trabajador/acción + paginación como estado local de UI y el hook de datos
 * `useTalonarioHistory` (TanStack Query). Replica las columnas del SSR
 * (`progress/explore_talonarios_ssr.md`, §2.4): acción, trabajador (enlace al
 * detalle), descripción, valores anterior/nuevo (el "nuevo" de una asignación
 * enlaza a su histórico de rango), realizado por y cuándo. Presentacional +
 * hooks; sin `fetch`.
 */
import { useState } from "react";
import { Link } from "react-router-dom";

import {
  Button,
  Card,
  CardContent,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui";
import { talonarioDetailPath, talonarioHistoricalPath, ROUTES } from "@/features/navigation";

import { formatDateTime, talonarioErrorMessage } from "../format";
import { useTalonarioHistory } from "../hooks/useTalonarioHistory";
import type { TalonarioHistoryEntry } from "../types";
import { RequireManageTalonarios } from "./RequireManageTalonarios";
import { TalonarioPagination } from "./TalonarioPagination";

const PER_PAGE = 20;

/**
 * Si un valor de historial tiene la forma "start-end" (rango de talonario),
 * devuelve un enlace a su histórico; si no, el texto plano. El "nuevo valor" de
 * una asignación es un rango y el SSR lo enlaza al histórico de ese rango.
 */
function RangeValue({
  worker,
  value,
}: {
  worker: string;
  value: string | null;
}) {
  if (!value) {
    return <>—</>;
  }
  const match = /^(\d+)-(\d+)$/.exec(value.trim());
  if (!match) {
    return <>{value}</>;
  }
  const [, start, end] = match;
  return (
    <Link
      to={talonarioHistoricalPath(worker, Number(start), Number(end))}
      className="text-brand hover:underline"
    >
      {value}
    </Link>
  );
}

export function TalonarioHistoryScreen() {
  const [page, setPage] = useState(1);
  const [worker, setWorker] = useState("");
  const [action, setAction] = useState("");
  const [workerInput, setWorkerInput] = useState("");
  const [actionInput, setActionInput] = useState("");

  const history = useTalonarioHistory({ worker, action, page, per_page: PER_PAGE });

  function applyFilters(event: React.FormEvent) {
    event.preventDefault();
    setWorker(workerInput.trim());
    setAction(actionInput.trim());
    setPage(1);
  }

  function clearFilters() {
    setWorkerInput("");
    setActionInput("");
    setWorker("");
    setAction("");
    setPage(1);
  }

  const hasFilters = worker !== "" || action !== "";

  return (
    <RequireManageTalonarios title="Historial de talonarios">
      <div className="flex flex-col gap-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-2xl font-semibold text-slate-900">
            Historial de talonarios
          </h1>
          <Button asChild variant="ghost" size="md">
            <Link to={ROUTES.talonarios}>Volver a talonarios</Link>
          </Button>
        </div>

        <form className="flex flex-wrap items-end gap-2" onSubmit={applyFilters}>
          <div className="flex flex-col gap-1">
            <label htmlFor="history-worker" className="text-sm font-medium text-slate-700">
              Trabajador
            </label>
            <Input
              id="history-worker"
              className="max-w-xs"
              placeholder="Filtrar por trabajador"
              value={workerInput}
              onChange={(event) => setWorkerInput(event.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="history-action" className="text-sm font-medium text-slate-700">
              Acción
            </label>
            <Input
              id="history-action"
              className="max-w-xs"
              placeholder="Filtrar por acción"
              value={actionInput}
              onChange={(event) => setActionInput(event.target.value)}
            />
          </div>
          <Button type="submit" variant="primary" size="md">
            Filtrar
          </Button>
          {hasFilters && (
            <Button type="button" variant="ghost" size="md" onClick={clearFilters}>
              Limpiar
            </Button>
          )}
        </form>

        <Card>
          <CardContent className="p-0">
            {history.isPending ? (
              <p className="px-1 py-8 text-center text-sm text-slate-500" role="status">
                Cargando historial…
              </p>
            ) : history.isError ? (
              <p className="px-1 py-8 text-center text-sm text-red-600" role="alert">
                {talonarioErrorMessage(history.error)}
              </p>
            ) : (
              <>
                <HistoryTable items={history.data.items} />
                <div className="border-t border-slate-200">
                  <TalonarioPagination
                    page={history.data.page}
                    pages={history.data.pages}
                    total={history.data.total}
                    onPageChange={setPage}
                    disabled={history.isFetching}
                  />
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </RequireManageTalonarios>
  );
}

/** Tabla del historial (presentacional). */
function HistoryTable({ items }: { items: TalonarioHistoryEntry[] }) {
  if (items.length === 0) {
    return (
      <p className="px-1 py-8 text-center text-sm text-slate-500">
        No hay operaciones registradas.
      </p>
    );
  }

  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableHeaderCell>Acción</TableHeaderCell>
          <TableHeaderCell>Trabajador</TableHeaderCell>
          <TableHeaderCell>Descripción</TableHeaderCell>
          <TableHeaderCell>Anterior</TableHeaderCell>
          <TableHeaderCell>Nuevo</TableHeaderCell>
          <TableHeaderCell>Realizado por</TableHeaderCell>
          <TableHeaderCell>Cuándo</TableHeaderCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {items.map((entry) => (
          <TableRow key={entry.id}>
            <TableCell className="font-medium text-slate-900">{entry.action}</TableCell>
            <TableCell>
              <Link
                to={talonarioDetailPath(entry.worker)}
                className="text-brand hover:underline"
              >
                {entry.worker}
              </Link>
            </TableCell>
            <TableCell>{entry.description}</TableCell>
            <TableCell>
              <RangeValue worker={entry.worker} value={entry.old_value} />
            </TableCell>
            <TableCell>
              <RangeValue worker={entry.worker} value={entry.new_value} />
            </TableCell>
            <TableCell>{entry.performed_by}</TableCell>
            <TableCell>{formatDateTime(entry.performed_at)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
